'use client'

// /[[...slug]] region catch-all — content island (App Router slice 5 Phase B, #107).
//
// This is the App Router adaptation of the (deleted) LandingPage render tree in
// pages/[[...slug]].js. #118 Step 5 (the boundary flip) made it CONTENT-ONLY: the
// site chrome (Header/Footer/announcement/cart/cookie-notice + the <main>
// landmark) now renders once as the server <SiteChrome> shell wired into
// app/layout.js around every page, so this island no longer wraps its content in
// <Layout>. It renders ONLY the page's content (sections + JSON-LD + LCP preload
// + geo banner).
//
// DATA (Overmind retirement, Phase D2): every request-time value the sections read
// — namedPrices / faqs / blogPosts / subscriptions+add-ons+coaching+turnkey /
// shop offers / shippingInfo / newRegion — is threaded as a server PROP into the
// providers wrapped around the content below (components/NamedPrices,
// RegionContent, RegionSurfaces, RegionOffers, RegionGeo). Those props are present
// DURING the server SSR pass AND the client's first render (same serialized
// values), so the SSR HTML === client first render (no React #418/#423). There is
// no client store to hydrate any more — the former synchronous useMemo that seeded
// the Overmind store is gone. Per-user / post-mount concerns (country list, cookie
// overlay, cart) live in Zustand stores, overlaid only AFTER mount.
//
// SEO: title/description/canonical/hreflang/OG are emitted by the route's
// generateMetadata (page.js), NOT here — next-seo's <NextSeo> renders through
// next/head, which CRASHES under App Router SSR (same reason Layout gates its
// next/head + LogoJsonLd/OrganizationJsonLd behind isPagesRouter). For the same
// reason we do NOT use next-seo's <BreadcrumbJsonLd>/<ProductJsonLd> here;
// instead we emit the Breadcrumb + Product JSON-LD as raw
// <script type="application/ld+json"> nodes (App-Router-safe), and we render the
// LCP preload <link> directly (React 18 hoists it to <head>) instead of via
// components/LcpPreload (which also uses next/head).

import React from 'react'
import PropTypes from 'prop-types'

import RenderSections from '../../components/RenderSections'
import { LcpContext } from '../../components/LcpContext'
import { NamedPricesProvider } from '../../components/NamedPrices'
import { RegionContentProvider } from '../../components/RegionContent'
import { RegionSurfacesProvider } from '../../components/RegionSurfaces'
import { RegionOffersProvider, useRegionOffers } from '../../components/RegionOffers'
import { RegionGeoProvider } from '../../components/RegionGeo'
import GeoSuggestBanner from '../../components/GeoSuggestBanner'
import CoachingSeo from '../../components/CoachingSeo'
import { COACHING_ROUTE_SLUG } from '../../utils/coachingSchema'
import { useSessionStore } from '../../store/sessionStore'
import { useRegionStore } from '../../store/regionStore'
import { useOffersStore } from '../../store/offersStore'
import { DEFAULT_REGION, REGIONS } from '../../utils/regions'
import { seoPropsForRoute } from '../../utils/regionSeo'
import { deriveH1 } from '../../utils/seoDerive'
import { breadcrumbJsonLd } from '../../utils/breadcrumbJsonLd'
import {
  findProductSection,
  resolveSectionProduct,
  buildProductJsonLdProps,
} from '../../utils/productJsonLd'

// Emit a raw JSON-LD <script>. App-Router-safe (no next/head). We stringify the
// same @graph next-seo would build so the structured data matches.
function JsonLd({ id, data }) {
  if (!data) return null
  return (
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
      key={id}
    />
  )
}

JsonLd.propTypes = { id: PropTypes.string, data: PropTypes.any }

// Single-<h1> rule (Audit A #1). Each main template renders its ONE keyword-rich
// hero heading as a REAL, visible <h1> via the first-class `<Text primary>` render
// contract (components/Text/render.js) — the product buy pages, the blog post, the
// home hero and the hardware pedals hero. To keep EXACTLY ONE <h1> per page we
// render the generic sr-only <h1> ONLY as a fallback on pages whose content has NO
// such primary-<h1> section. This set lists those section `_type`s; keep it in sync
// with the components that pass `primary` to <Text>/<TextServer> (grep `primary`
// across components/sections). Everything else (content pages: FAQ, legal, generic
// marketing) keeps the sr-only fallback as its single <h1>.
//
// `homeSection` appears MULTIPLE times on the home page, but only the FIRST
// (RenderSections' `isPageHero`) promotes its title to <h1> — so a homeSection in
// the content still means "a visible primary <h1> will render", and suppressing the
// fallback keeps the count at exactly one.
const PRIMARY_H1_SECTION_TYPES = new Set([
  'pedalsHero',
  // Black Friday page hero: always renders one <h1> (variant 'page'; the
  // homepage 'home' variant sits under homeSection's h1).
  'saleHero',
  'productCheckoutPage',
  'gt2CockpitCheckoutPage',
  'gt2FullRigCheckoutPage',
  'upgradableWheelBaseCheckoutPage',
  'uDFP20UpgradesCheckoutPage',
  'blogPostPage',
  'homeSection',
  // #h1-coverage sweep — every remaining page TYPE with a dedicated, visible hero
  // heading now promotes that heading to the document <h1> (via `<Text primary>` or
  // an `.h1-contents` <h1> wrapper in the section component). Adding the `_type`
  // here suppresses the sr-only fallback so the count stays at exactly one <h1>.
  // Dedicated single-use heroes (max once per page — verified against the dataset)
  // promote UNCONDITIONALLY:
  'liteFormulaWheelHero', // hardware wheel-base / R295 / GT2 / lite-formula heroes
  'productOverviewHero', // dfp20 / udfp20 wheel-base heroes
  'getFasterHero', // /academy
  'teamVrsHero', // /teamvrs, /vrrents
  'hardwarePage', // /hardware index hero
  'aboutUsPage', // /about-us
  'creatorsProgramPage', // /creators-program
  'teamVrsNascarPage', // /nascar-team-vrs
  'turnkeySolutions', // /turnkey-sim
  'turnkeyConfigurator', // /turnkey_test (configurator test page)
  'downloadsPage', // /downloads, /manuals
  'BF2024Hero', // /holiday-sale-2025 (seasonal)
  // Sections that ALSO appear as NON-hero blocks on other pages promote their title
  // ONLY when `isPageHero` (the first visible section) — so the extra occurrences
  // add no stray <h1>. Listing the `_type` is still correct: the fallback is
  // suppressed only on pages where the section IS present AND, when it is the hero,
  // a real <h1> renders (the home page's homeSection is the original example).
  'ffbNextGen', // hero on /nextgenffb; a mid-page block on home + wheel-base pages
  'headerGallery1', // hero on /coaching (first carousel page of the first gallery)
])

function contentHasHeroH1(content) {
  if (!Array.isArray(content)) return false
  return content.some((s) => s && !s.hidden && PRIMARY_H1_SECTION_TYPES.has(s._type))
}

// Product structured-data reader. Same resolution as
// components/ProductStructuredData, but emits raw JSON-LD instead of next-seo's
// <ProductJsonLd> (which crashes under App Router). Reads offers from the
// RegionOffers seam (Phase D1): the server-threaded grid at SSR (the SAME array
// that ships the JSON-LD in the SSR HTML — this product's own item stays uncapped
// via #90 focusTypes), upgraded to the offers store's FULL offers post-mount. It
// sits under <RegionOffersProvider> below, so it reads the server prop directly.
function ProductJsonLdRaw({ content, region, url }) {
  const { offers } = useRegionOffers()
  const section = findProductSection(content)
  if (!section) return null
  const resolved = resolveSectionProduct(section, offers)
  if (!resolved) return null
  const props = buildProductJsonLdProps({
    primary: resolved.primary,
    variants: resolved.variants,
    region,
    url,
    name: section.title,
  })
  if (!props) return null

  // Compose the schema.org Product object from the next-seo prop bag. Mirrors
  // next-seo's ProductJsonLd output closely enough for parity on the scratch path.
  const product = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: props.productName,
    image: props.images,
    brand: { '@type': 'Brand', name: props.brand },
    manufacturer: { '@type': 'Organization', name: props.manufacturerName },
  }
  if (props.sku) product.sku = props.sku
  if (props.mpn) product.mpn = props.mpn
  if (props.description) product.description = props.description
  if (props.aggregateOffer) {
    product.offers = {
      '@type': 'AggregateOffer',
      priceCurrency: props.aggregateOffer.priceCurrency,
      lowPrice: props.aggregateOffer.lowPrice,
      highPrice: props.aggregateOffer.highPrice,
      offerCount: props.aggregateOffer.offerCount,
      offers: (props.aggregateOffer.offers || []).map((o) => ({ '@type': 'Offer', ...o })),
    }
  } else if (props.offers) {
    product.offers = props.offers.map((o) => ({ '@type': 'Offer', ...o }))
  }

  return <JsonLd id="product-jsonld" data={product} />
}

ProductJsonLdRaw.propTypes = {
  content: PropTypes.any,
  region: PropTypes.string,
  url: PropTypes.string,
}

// Query-param effects (aff-code / discount-code). Reads the entry-time query
// string from `window.location.search` inside a post-mount effect — NOT
// next/navigation's useSearchParams. Why: useSearchParams forces the nearest
// parent to a client-side Suspense boundary, which under App Router SSR produced
// 1×React #419 live. aff-code/discount are entry-time params, read ONCE on mount
// (this effect is client-only/post-mount, so `window` is defined and reading the
// URL here is correct and hydration-safe — it never touches the SSR HTML).
function QueryParamEffects() {
  React.useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const query = {}
    for (const [k, v] of params.entries()) query[k] = v
    // affiliate/discount codes moved off Overmind to the Zustand session store
    // (Phase C2 part 1); the entry-time query is read from the URL directly (the
    // former Overmind `queryParamsReceived({ query })` mirror was dropped in
    // Phase C2 part 3 — nothing reads `state.query` any more). Restore the
    // persisted codes FIRST (so the affiliate % fetch below sees a returning
    // visitor's saved code), then apply the entry-time query params — same
    // ordering the Overmind appRendered->query pipe produced.
    const session = useSessionStore.getState()
    session.hydrateFromLocalStorage()
    session.applyAffiliateFromQuery(query)
    session.applyDiscountFromQuery(query)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return null
}

function RegionPageInner(props) {
  const {
    content = [],
    config = {},
    slug,
    region = DEFAULT_REGION,
    isCommerce = true,
    servedStores = REGIONS,
    servedWorldCountries = [],
    lcpImage = null,
    lcpIsBackground = false,
    lcpBackgroundImage = null,
    namedPrices = null,
    surfaces = null,
    shopItems = null,
    shippingInfo = null,
    newRegion = null,
    faqs = null,
    blogPosts = null,
    title,
    // #118: when the page is all-RSC-eligible, page.js server-renders the section
    // tree and passes it here; we render it INSTEAD of the client <RenderSections>
    // so those sections ship 0 JS. Absent => the normal client section path.
    serverSections = null,
  } = props

  // Overmind retirement (Phase D2): the former synchronous useMemo hydration
  // (hydrateRegionMutations / appRendered / updateSlug into the Overmind store) is
  // GONE. Every value it hydrated is now threaded as a server PROP into the
  // providers below (namedPrices / faqs / blogPosts / surfaces / shopItems /
  // shippingInfo / newRegion) or read from a Zustand store, so the page renders
  // straight from props with no store to seed — on both the SSR pass and the
  // client's first render, still byte-identical (no React #418/#423).

  // Post-mount, client-only overlays (never affect the SSR HTML): seed the client
  // pricing country from the SSR server prop (region-deterministic; keeps a
  // hydrated commerce page's exact region), load the country list (both on the
  // Zustand region store now — Phase C2 part 2), + the /shop client-fetch fallback
  // when SSR did not hydrate shop items (mirrors _app.js render(): getCountries +
  // the shopItemsFetched-gated fetch). initClientRegion/getCountries are idempotent
  // (guarded), so co-existing with app/providers.jsx's global bootstrap is safe.
  React.useEffect(() => {
    const region = useRegionStore.getState()
    region.initClientRegion(newRegion)
    region.getCountries()
    // /shop client-fetch FALLBACK: only when SSR did NOT hydrate the grid offers
    // (the `shopItems` server prop is empty — a price-bundle miss). Mirrors the
    // former `slug === 'shop' && !state.shopItemsFetched` gate, but fires the offers
    // store's ensureFullOffers (Phase D1) instead of shopItemsRequiredPageRendered.
    // ensureFullOffers is idempotent, so this never double-fetches.
    if (slug === 'shop' && (!shopItems || !shopItems.length)) {
      useOffersStore.getState().ensureFullOffers(newRegion)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const h1Text = deriveH1({ title, slug })
  // Only emit the generic sr-only <h1> when no section renders a visible hero
  // <h1> — otherwise the page would carry two <h1>s (breaking the single-<h1>
  // rule). The visible hero <h1> is the keyword-rich, on-screen title.
  const needsFallbackH1 = !contentHasHeroH1(content)

  // Canonical URL for the ProductJsonLd `url` (keeps JSON-LD in lock-step with
  // the page's SEO canonical, which generateMetadata emits identically).
  const { canonical: canonicalUrl } = seoPropsForRoute(
    config.url,
    region,
    slug,
    servedStores,
    servedWorldCountries
  )

  const breadcrumb = breadcrumbJsonLd(slug, config.url, region)

  // LCP preload <link>(s): rendered directly (React 18 hoists <link> to <head>
  // under App Router). components/LcpPreload uses next/head and would crash here.
  //
  // The home page's LCP is VIEWPORT-DEPENDENT: the foreground `imageCs` hero is
  // the desktop LCP (≥992px), but the full-bleed `bgImage` CSS-background DIV is
  // the LARGER paint — the real LCP — on mobile (≤991px). resolveLcpImage surfaces
  // that bg as `lcpBackgroundImage` ONLY for this home shape (a section with BOTH
  // a foreground hero AND a bgImage). When present we emit TWO media-scoped
  // preloads so each viewport fetches ONLY its own LCP with no cross-viewport
  // contention: the browser honors `media` on preload links, downloading just the
  // matching one. The mobile link preloads the SINGLE bg `src` (src-only) — the
  // bg DIV paints `url(${bgImage.image.src})`, so this byte-matches its fetch, no
  // wasted imagesrcset candidate. EVERY other page has no `lcpBackgroundImage`, so
  // it emits the ONE preload below, byte-identical to before (no `media` attr).
  const lcpPreload =
    lcpImage && lcpImage.src ? (
      lcpBackgroundImage && lcpBackgroundImage.src ? (
        <>
          <link
            rel="preload"
            as="image"
            href={lcpImage.src}
            {...(lcpImage.srcSet && !lcpIsBackground
              ? { imageSrcSet: lcpImage.srcSet, imageSizes: '100vw' }
              : {})}
            fetchPriority="high"
            media="(min-width: 992px)"
          />
          <link
            rel="preload"
            as="image"
            href={lcpBackgroundImage.src}
            fetchPriority="high"
            media="(max-width: 991px)"
          />
        </>
      ) : (
        <link
          rel="preload"
          as="image"
          href={lcpImage.src}
          {...(lcpImage.srcSet && !lcpIsBackground
            ? { imageSrcSet: lcpImage.srcSet, imageSizes: '100vw' }
            : {})}
          fetchPriority="high"
        />
      )
    ) : null

  // Content-only (#118 Step 5): a fragment, NOT <Layout>. This subtree lands
  // inside SiteChrome's <main id="main-content"> (SiteChrome renders {children}
  // there), so the DOM position of the sections is unchanged — only the chrome
  // that used to wrap them moved out to the server shell.
  return (
    <NamedPricesProvider namedPrices={namedPrices}>
      <RegionContentProvider faqs={faqs} blogPosts={blogPosts}>
        <RegionSurfacesProvider {...(surfaces || {})}>
          <RegionOffersProvider shopItems={shopItems}>
           <RegionGeoProvider shippingInfo={shippingInfo} newRegion={newRegion}>
            {lcpPreload}
            {breadcrumb && <JsonLd id="breadcrumb-jsonld" data={breadcrumb} />}
            <ProductJsonLdRaw content={content} region={region} url={canonicalUrl} />
            {needsFallbackH1 && <h1 className="sr-only">{h1Text}</h1>}
            <GeoSuggestBanner currentRegion={region} slug={slug} isCommerce={isCommerce} />
            <LcpContext.Provider value={lcpImage ? lcpImage.src : null}>
              {serverSections || (content && (
                <RenderSections sections={content} slug={slug} region={region} />
              ))}
            </LcpContext.Provider>
            {/* Play A: /coaching Service + FAQPage schema + the matching visible
                FAQ accordion. Emitted ONLY for the coaching route so no other page
                carries coaching structured data. `canonicalUrl` is the same value
                the page's SEO canonical + Product JSON-LD use. */}
            {slug === COACHING_ROUTE_SLUG && <CoachingSeo url={canonicalUrl} />}
            <QueryParamEffects />
           </RegionGeoProvider>
          </RegionOffersProvider>
        </RegionSurfacesProvider>
      </RegionContentProvider>
    </NamedPricesProvider>
  )
}

RegionPageInner.propTypes = {
  content: PropTypes.any,
  config: PropTypes.any,
  slug: PropTypes.string,
  region: PropTypes.string,
  isCommerce: PropTypes.bool,
  servedStores: PropTypes.any,
  servedWorldCountries: PropTypes.any,
  lcpImage: PropTypes.any,
  lcpIsBackground: PropTypes.bool,
  lcpBackgroundImage: PropTypes.any,
  namedPrices: PropTypes.any,
  surfaces: PropTypes.any,
  shopItems: PropTypes.any,
  shippingInfo: PropTypes.any,
  newRegion: PropTypes.string,
  faqs: PropTypes.any,
  blogPosts: PropTypes.any,
  title: PropTypes.string,
  serverSections: PropTypes.node,
}

export default function RegionPageClient(props) {
  return <RegionPageInner {...props} />
}

RegionPageClient.propTypes = RegionPageInner.propTypes
